import React, { useState, useRef, useEffect, useMemo } from 'react';
import './BadgeTooltip.scss';

interface Badge {
  id: string;
  icon: string;
  name?: string;
  description?: string;
}

interface BadgeTooltipProps {
  badge: Badge;
}

/**
 * Hover-to-show on desktop (mouse devices); tap-to-show on touch.
 * Touch gating mirrors the HintTooltip fix from PR #266 — iOS Safari
 * fires mouseenter → click → mouseleave on a single tap, which made
 * the bubble flash visible for one frame and then immediately hide.
 * Skip mouse handlers entirely on touch devices; first tap shows,
 * tap-outside (or another badge) hides. See #272.
 */
const BadgeTooltip: React.FC<BadgeTooltipProps> = ({ badge }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const canHover = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    [],
  );

  const show = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(true);
  };

  const hide = () => {
    timerRef.current = setTimeout(() => setVisible(false), 100);
  };

  // Tap-outside dismissal for touch devices.
  useEffect(() => {
    if (!visible || canHover) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [visible, canHover]);

  return (
    <span
      ref={wrapperRef}
      className="badge-tooltip-wrapper"
      onMouseEnter={canHover ? show : undefined}
      onMouseLeave={canHover ? hide : undefined}
      onFocus={show}
      onBlur={canHover ? hide : undefined}
      // Tap shows on touch; outside-tap closes (see effect above).
      // Toggling here would race with the outside-tap handler — explicit
      // setVisible(true) is reliable.
      onClick={(e) => { e.stopPropagation(); setVisible(true); }}
    >
      <span className="badge">{badge.icon}</span>
      {visible && (badge.name || badge.description) && (
        <div className="badge-tooltip-card" role="tooltip">
          <span className="badge-tooltip-icon">{badge.icon}</span>
          <div className="badge-tooltip-body">
            {badge.name && <span className="badge-tooltip-name">{badge.name}</span>}
            {badge.description && <span className="badge-tooltip-desc">{badge.description}</span>}
          </div>
        </div>
      )}
    </span>
  );
};

export default BadgeTooltip;
